import { API_URL } from '../constants/constants.ts';

// Constants for processing status
export const ProcessingStatus = {
  IDLE: 'IDLE',
  UPLOADING: 'UPLOADING',
  PROCESSING: 'PROCESSING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR'
};

// Interface for upload item
export interface UploadItem {
  key: string;
  fileName: string;
  size: number;
  lastModified: string;
  publicUrl: string;
}

// Interface for pagination data
export interface PaginationData {
  page: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
}

// Interface for list uploads response
export interface ListUploadsResponse {
  uploads: UploadItem[];
  pagination: PaginationData;
  feedId: string;
}

// Processing state management
class ProcessingState {
  private listeners: Set<Function>;

  constructor() {
    this.listeners = new Set();
  }

  subscribe(listener: Function) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(status: any) {
    this.listeners.forEach(listener => listener(status));
  }
}

export const processingState = new ProcessingState();

export const updateStatus = (status: any) => {
  processingState.emit(status);
};

// Type definitions
interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  feedId: string;
  publicUrl: string;
  maxSizeBytes: number;
  maxSizeMB: number;
  cacheControl?: boolean | string;
}

type PresignedUrlErrorResponse = {
  error?: string;
  code?: string;
  message?: string;
  hint?: string;
};

const buildPresignedUrlErrorMessage = (
  status: number,
  statusText: string,
  payload?: PresignedUrlErrorResponse,
  fallbackBodyText?: string
) => {
  const message = payload?.message?.trim();
  const hint = payload?.hint?.trim();
  const code = payload?.code?.trim();
  const error = payload?.error?.trim();

  // Prefer API-provided message/hint (e.g. invalid_filename)
  if (message) {
    return hint ? `${message}\n${hint}` : message;
  }

  // Next best: known error identifiers
  if (error || code) {
    const parts = [error, code].filter(Boolean).join(' / ');
    return `Upload request failed (${status}${statusText ? ` ${statusText}` : ''}): ${parts}`;
  }

  // Fallback: raw body or HTTP status
  const body = fallbackBodyText?.trim();
  if (body) {
    return `Upload request failed (${status}${statusText ? ` ${statusText}` : ''}): ${body}`;
  }

  return `Upload request failed (${status}${statusText ? ` ${statusText}` : ''}).`;
};

/**
 * Fetch list of uploaded files
 */
export const getUploadsList = async (authToken: string, page: number = 1): Promise<ListUploadsResponse> => {
  try {
    const response = await fetch(`${API_URL}/api/list-uploads?page=${page}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch uploads: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching uploads list:', error);
    throw error;
  }
};

// Get a presigned URL for file upload
// cacheControl defaults to true for optimal caching and performance
// useUserEndpoint: if true, uses /api/user/upload/presigned-url (no podcast required)
export const getPresignedUrl = async (
  fileName: string, 
  fileType: string, 
  authToken: string,
  cacheControl: boolean | string = true,
  useUserEndpoint: boolean = false
): Promise<PresignedUrlResponse> => {
  const endpoint = useUserEndpoint 
    ? `${API_URL}/api/user/upload/presigned-url`
    : `${API_URL}/api/generate-presigned-url`;
    
  console.log('Requesting presigned URL for:', { fileName, fileType, cacheControl, endpoint });
  
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    },
    body: JSON.stringify({ 
      fileName, 
      fileType, 
      acl: 'public-read',
      cacheControl
    })
  });
  
  if (!response.ok) {
    // Try to parse the API's structured error payload first (common for 400s).
    let errorPayload: PresignedUrlErrorResponse | undefined;
    let fallbackText: string | undefined;

    try {
      errorPayload = (await response.clone().json()) as PresignedUrlErrorResponse;
    } catch {
      try {
        fallbackText = await response.text();
      } catch {
        // ignore
      }
    }

    const errorMessage = buildPresignedUrlErrorMessage(
      response.status,
      response.statusText,
      errorPayload,
      fallbackText
    );

    console.error('Failed to get presigned URL:', {
      status: response.status,
      statusText: response.statusText,
      errorPayload,
      fallbackText
    });

    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log('Presigned URL Data:', {
    uploadUrl: data.uploadUrl,
    key: data.key,
    feedId: data.feedId,
    maxSizeMB: data.maxSizeMB,
    cacheControl: data.cacheControl
  });
  
  return data;
};

/**
 * Direct XMLHttpRequest upload to S3/DigitalOcean Spaces using presigned URL
 * This function is intended to be the most reliable method for uploads
 * 
 * @param file The file to upload
 * @param presignedUrl The presigned URL to upload to
 * @param cacheControl Cache-Control configuration (true = default, string = custom value, false = none)
 * @param onProgress Optional progress callback
 */
export const directUpload = async (
  file: File,
  presignedUrl: string,
  cacheControl?: boolean | string,
  onProgress?: (progress: number) => void
): Promise<XMLHttpRequest> => {
  return new Promise((resolve, reject) => {
    // Create new XMLHttpRequest
    const xhr = new XMLHttpRequest();
    
    // Set up request
    xhr.open('PUT', presignedUrl, true);
    xhr.timeout = 30 * 60 * 1000; // 30 minutes timeout
    
    // Set headers - keeping this minimal to avoid CORS issues
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.setRequestHeader('x-amz-acl', 'public-read');
    
    // CRITICAL: Include Cache-Control header if cacheControl is enabled
    if (cacheControl) {
      const cacheControlValue = typeof cacheControl === 'string' 
        ? cacheControl 
        : 'public, max-age=31536000, immutable';
      
      xhr.setRequestHeader('Cache-Control', cacheControlValue);
      console.log('Setting Cache-Control header:', cacheControlValue);
    }
    
    // Don't send cookies
    xhr.withCredentials = false;
    
    // Handle progress events
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(percentComplete);
      }
    };
    
    // Handle completion
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        console.log('Upload completed successfully');
        resolve(xhr);
      } else {
        console.error('Upload failed with status:', xhr.status);
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    
    // Handle network errors
    xhr.onerror = () => {
      console.error('Network error during upload');
      reject(new Error('Network error during upload'));
    };
    
    // Handle timeouts
    xhr.ontimeout = () => {
      console.error('Upload timed out');
      reject(new Error('Upload timed out'));
    };
    
    // Handle aborts
    xhr.onabort = () => {
      console.error('Upload aborted');
      reject(new Error('Upload was aborted'));
    };
    
    // Send the file
    xhr.send(file);
    
    // Return the xhr object so it can be aborted if needed
    return xhr;
  });
};

/**
 * Process a file upload with status tracking
 * This is the primary function to use for uploads
 * 
 * @param file The file to upload
 * @param authToken Authentication token
 * @param cacheControl Cache-Control configuration (defaults to true for optimal caching)
 */
export const processFileUpload = async (
  file: File, 
  authToken: string,
  cacheControl: boolean | string = true,
  useUserEndpoint: boolean = false
) => {
  let xhr: XMLHttpRequest | null = null;
  let aborted = false;
  
  try {
    // Step 1: Start the process and update status
    updateStatus({
      status: ProcessingStatus.UPLOADING,
      message: 'Initializing upload...',
      progress: 5
    });
    
    // Step 2: Get presigned URL
    updateStatus({
      status: ProcessingStatus.UPLOADING,
      message: 'Requesting upload URL...',
      progress: 10
    });
    
    const presignedData = await getPresignedUrl(file.name, file.type, authToken, cacheControl, useUserEndpoint);
    const { uploadUrl, key, publicUrl, cacheControl: responseCacheControl } = presignedData;
    
    // Extract a unique ID from the key (typically format: folder/id/filename)
    const uploadId = key.split('/')[1] || 'unknown';
    
    // Step 3: Upload the file
    updateStatus({
      status: ProcessingStatus.UPLOADING,
      message: 'Uploading file...',
      progress: 20,
      uploadId
    });
    
    // Start the upload with cache control
    xhr = await directUpload(file, uploadUrl, responseCacheControl, (progress) => {
      // Scale progress from 20-90%
      const scaledProgress = 20 + (progress * 0.7);
      
      updateStatus({
        status: ProcessingStatus.UPLOADING,
        message: progress < 100 ? 'Uploading file...' : 'Upload complete, processing...',
        progress: scaledProgress,
        uploadId
      });
    });
    
    // Check if aborted during upload
    if (aborted) {
      throw new Error('Upload was cancelled');
    }
    
    // Step 4: Processing phase - Just a brief pause for UX purposes
    updateStatus({
      status: ProcessingStatus.PROCESSING,
      message: 'Finalizing upload...',
      progress: 95,
      uploadId
    });
    
    // Brief delay for UX purposes
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Step 5: Complete
    updateStatus({
      status: ProcessingStatus.COMPLETE,
      message: 'Upload complete!',
      progress: 100,
      uploadId,
      data: {
        fileUrl: publicUrl || `${uploadUrl.split('?')[0]}`, // Extract the base URL without query params
        key,
        fileName: file.name
      }
    });
    
    return {
      fileUrl: publicUrl || `${uploadUrl.split('?')[0]}`,
      uploadId,
      key,
      fileName: file.name
    };
    
  } catch (error) {
    // Handle errors
    const errorMessage = error instanceof Error ? error.message : 'Unknown error during upload';
    console.error('Upload process error:', errorMessage);
    
    updateStatus({
      status: ProcessingStatus.ERROR,
      message: errorMessage,
      progress: 0
    });
    
    throw error;
  }
};

/**
 * Cancel an in-progress upload
 */
export const cancelUpload = (xhr: XMLHttpRequest | null) => {
  if (xhr && xhr.readyState !== 4) {
    xhr.abort();
    updateStatus({
      status: ProcessingStatus.IDLE,
      message: 'Upload cancelled',
      progress: 0
    });
    return true;
  }
  return false;
};

const UploadService = {
  getPresignedUrl,
  directUpload,
  processFileUpload,
  cancelUpload,
  getUploadsList,
  ProcessingStatus,
  processingState
};

export default UploadService; 